import React, { useState, useRef, useEffect, useCallback } from 'react';
import { RTVIClient, RTVIEvent } from '@pipecat-ai/client-js';
import { WebSocketTransport } from '@pipecat-ai/websocket-transport';
import { Mic, MicOff, Phone, PhoneOff, Volume2, VolumeX, Settings } from 'lucide-react';

const LiveInterview = ({ 
  applicationId = "manual-session",
  interviewType = "interviewer", // "interviewer" or "trainer"
  candidateName = "Demo Candidate",
  candidateEmail = "",
  candidateId = "",
  jobTitle = "Software Developer",
  companyName = "Tech Company",
  jobDescription = "",
  duration = 5, // Duration in minutes
  onInterviewEnd = () => {}
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMicEnabled, setIsMicEnabled] = useState(false);
  const [isSpeakerEnabled, setIsSpeakerEnabled] = useState(true);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [showSettings, setShowSettings] = useState(false);
  const [language, setLanguage] = useState('en-US');
  const [voiceGender, setVoiceGender] = useState('female');
  const [speechSpeed, setSpeechSpeed] = useState(1.0);
  const [timeRemaining, setTimeRemaining] = useState(duration * 60); // Convert to seconds
  const [hasShownWarning, setHasShownWarning] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);
  const [transcript, setTranscript] = useState([]); // Store conversation transcript
  
  const rtviClientRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioElementRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const userSpeakingTimeoutRef = useRef(null);

  const addLog = useCallback((message, level = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    console.log(`[${level.toUpperCase()}] ${message}`);
    setDebugLogs(prev => [...prev, { message, level, timestamp }].slice(-50)); // Keep last 50 logs
  }, []);
  
  // Update timer when duration prop changes
  useEffect(() => {
    setTimeRemaining(duration * 60);
    addLog(`Timer set to ${duration} minutes (${duration * 60} seconds)`);
  }, [duration, addLog]);

  const getApiBaseUrl = () => {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;
    
    // For development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:8000`;
    }
    
    // For production - use the backend URL from environment or default
    const backendUrl = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';
    
    // Remove /api suffix if present (we'll add it when needed)
    return backendUrl.replace('/api', '');
  };

  const createSessionRecord = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/live-interview/create-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: applicationId,
          application_id: applicationId,
          candidate_id: candidateId,
          candidate_name: candidateName,
          candidate_email: candidateEmail,
          job_title: jobTitle,
          company: companyName,
          interview_type: interviewType,
          language: language,
          duration_minutes: duration
        })
      });

      if (response.ok) {
        const data = await response.json();
        addLog(`Session created in database: ${data.session_id}`);
      } else {
        addLog(`Failed to create session: ${response.statusText}`, 'warning');
        // Continue anyway - save-transcript has fallback
      }
    } catch (error) {
      addLog(`Error creating session: ${error.message}`, 'warning');
      // Continue anyway - save-transcript has fallback
    }
  };

  const setupAudioTrack = useCallback((track) => {
    addLog('Setting up audio track');
    if (audioElementRef.current) {
      const stream = new MediaStream([track]);
      audioElementRef.current.srcObject = stream;
      audioElementRef.current.play().catch(e => 
        addLog(`Audio play failed: ${e}`, 'error')
      );
    }
  }, [addLog]);

  const setupTrackListeners = useCallback(() => {
    if (!rtviClientRef.current) return;

    rtviClientRef.current.on(RTVIEvent.TrackStarted, (track, participant) => {
      addLog(`Track started: ${track.kind} from ${participant?.name || 'unknown'}`);
      if (!participant?.local && track.kind === 'audio') {
        setupAudioTrack(track);
        setIsSpeaking(true);
        // When AI starts speaking, mute user microphone to prevent interruption
        if (isMicEnabled) {
          const tracks = rtviClientRef.current.tracks();
          if (tracks.local?.audio) {
            tracks.local.audio.enabled = false;
            setIsMicEnabled(false);
            setIsListening(false);
            addLog('Microphone muted - AI is speaking');
          }
        }
      }
    });

    rtviClientRef.current.on(RTVIEvent.TrackStopped, (track, participant) => {
      addLog(`Track stopped: ${track.kind} from ${participant?.name || 'unknown'}`);
      if (!participant?.local && track.kind === 'audio') {
        setIsSpeaking(false);
        // When AI stops speaking, re-enable user microphone
        setTimeout(() => {
          if (rtviClientRef.current && isConnected) {
            const tracks = rtviClientRef.current.tracks();
            if (tracks.local?.audio) {
              tracks.local.audio.enabled = true;
              setIsMicEnabled(true);
              setIsListening(true);
              addLog('Microphone enabled - Your turn to speak');
            }
          }
        }, 500); // Small delay to ensure clean transition
      }
    });
  }, [addLog, setupAudioTrack, isMicEnabled, isConnected]);

  const connect = async () => {
    try {
      setIsConnecting(true);
      setConnectionStatus('Connecting');
      addLog('Starting connection process');

      // Create session record in database
      await createSessionRecord();

      // Initialize audio context
      audioContextRef.current = new AudioContext();
      await audioContextRef.current.resume();

      // Build query parameters
      const params = new URLSearchParams({
        application_id: applicationId,
        interview_type: interviewType,
        language: language,
        duration_minutes: duration.toString(),
        voice_gender: voiceGender,
        speech_speed: speechSpeed.toString(),
        candidate_name: candidateName,
        candidate_email: candidateEmail,
        candidate_id: candidateId,
        job_title: jobTitle,
        company_name: companyName,
      });
      
      if (jobDescription) {
        params.append('job_description', jobDescription.slice(0, 500));
      }

      // Get WebSocket URL from backend
      let baseUrl = getApiBaseUrl();
      
      // Convert to WebSocket protocol - WSS for HTTPS, WS for HTTP
      const isSecure = baseUrl.startsWith('https://');
      const wsBaseUrl = isSecure ? baseUrl.replace('https://', 'wss://') : baseUrl.replace('http://', 'ws://');
      
      addLog(`Connecting to: ${wsBaseUrl}/api/live-interview/connect`);
      
      // Use RTVI Client with WebSocket Transport
      const transport = new WebSocketTransport();

      const rtviConfig = {
        transport,
        params: {
          baseUrl: wsBaseUrl,  // Use WS/WSS URL directly
          endpoints: {
            connect: `/api/live-interview/connect?${params.toString()}`,
          },
        },
        enableMic: true,
        enableCam: false,
        callbacks: {
          onConnected: () => {
            setIsConnected(true);
            setConnectionStatus('Connected');
            addLog('Successfully connected to live interview');
            startTimer();
          },
          onDisconnected: () => {
            setIsConnected(false);
            setConnectionStatus('Disconnected');
            setIsMicEnabled(false);
            setIsListening(false);
            setIsSpeaking(false);
            addLog('Disconnected from live interview');
          },
          onBotReady: (data) => {
            addLog(`Bot ready: ${JSON.stringify(data)}`);
            // Setup media tracks
            const tracks = rtviClientRef.current?.tracks();
            if (tracks?.bot?.audio) {
              setupAudioTrack(tracks.bot.audio);
            }
          },
          onGenericMessage: (message) => {
            addLog(`Received message: ${JSON.stringify(message)}`);
            if (message.type === 'transcription') {
              const { participant, text } = message;
              addLog(`[${participant}] ${text}`, 'info');
              
              // Add to transcript
              const speaker = participant === 'bot' ? 'AI' : 'User';
              setTranscript(prev => [...prev, {
                speaker: speaker,
                text: text,
                timestamp: new Date().toISOString()
              }]);
            }
          },
          onMessageError: (error) => {
            addLog(`Message error: ${error}`, 'error');
          },
          onError: (error) => {
            addLog(`Error: ${error}`, 'error');
            setConnectionStatus('Error');
          },
        },
      };

      const client = new RTVIClient(rtviConfig);
      rtviClientRef.current = client;

      // Setup track listeners
      client.on(RTVIEvent.TrackStarted, (track, participant) => {
        addLog(`Track started: ${track.kind} from ${participant?.name || 'unknown'}`);
        if (!participant?.local && track.kind === 'audio') {
          setupAudioTrack(track);
          setIsSpeaking(true);
        }
      });

      client.on(RTVIEvent.TrackStopped, (track, participant) => {
        addLog(`Track stopped: ${track.kind} from ${participant?.name || 'unknown'}`);
        if (!participant?.local && track.kind === 'audio') {
          setIsSpeaking(false);
        }
      });

      // Initialize devices
      addLog('Initializing audio devices...');
      await client.initDevices();

      // Mute microphone by default
      const localTracks = client.tracks().local;
      if (localTracks?.audio) {
        localTracks.audio.enabled = false;
        addLog('Microphone muted by default');
      }

      // Connect to bot
      addLog('Connecting to bot...');
      await client.connect();

    } catch (error) {
      addLog(`Connection error: ${error.message}`, 'error');
      setConnectionStatus('Error');
      setIsConnecting(false);
      
      if (rtviClientRef.current) {
        try {
          await rtviClientRef.current.disconnect();
        } catch (disconnectError) {
          addLog(`Error during disconnect: ${disconnectError}`, 'error');
        }
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const startTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    
    timerIntervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1;
        
        // Show warning at 10 seconds remaining
        if (newTime === 10 && !hasShownWarning) {
          setHasShownWarning(true);
          // Send message to AI to wrap up
          if (rtviClientRef.current) {
            addLog('10 seconds remaining - sending wrap-up signal');
            // You can send a message to the bot here if needed
          }
        }
        
        // End session when time runs out
        if (newTime <= 0) {
          addLog('Time is up - ending session');
          disconnect();
          return 0;
        }
        
        return newTime;
      });
    }, 1000);
  };

  const disconnect = async () => {
    // Clear timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    if (rtviClientRef.current) {
      try {
        // Save transcript before disconnecting
        if (transcript.length > 0) {
          addLog(`Saving transcript with ${transcript.length} turns`);
          await saveTranscript(transcript);
        }
        
        // Disconnect RTVI client properly
        await rtviClientRef.current.disconnect();
        rtviClientRef.current = null;
        
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
        
        // Call the onInterviewEnd callback with session data
        onInterviewEnd({
          completed: true,
          duration: duration,
          timeSpent: duration * 60 - timeRemaining,
          transcriptLength: transcript.length
        });
      } catch (error) {
        addLog(`Disconnect error: ${error.message}`, 'error');
      }
    }
  };

  const saveTranscript = async (transcriptData) => {
    try {
      const baseUrl = getApiBaseUrl();
      addLog(`Attempting to save ${transcriptData.length} transcript turns`);
      
      const response = await fetch(`${baseUrl}/api/live-interview/save-transcript`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: applicationId,
          transcript: transcriptData,
          completed: true
        })
      });

      if (response.ok) {
        const data = await response.json();
        addLog(`✅ Transcript saved: ${data.turns_saved} turns`);
        
        // Trigger evaluation
        addLog('Starting evaluation...');
        await evaluateSession();
      } else {
        const errorText = await response.text();
        addLog(`❌ Failed to save transcript: ${response.status} - ${errorText}`, 'error');
      }
    } catch (error) {
      addLog(`❌ Error saving transcript: ${error.message}`, 'error');
    }
  };

  const evaluateSession = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      addLog('Sending evaluation request...');
      
      const response = await fetch(`${baseUrl}/api/live-interview/evaluate-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: applicationId
        })
      });

      if (response.ok) {
        const data = await response.json();
        addLog(`✅ Session evaluated successfully - Score: ${data.evaluation?.overall_score || 'N/A'}`);
        return data.evaluation;
      } else {
        const errorText = await response.text();
        addLog(`❌ Failed to evaluate session: ${response.status} - ${errorText}`, 'error');
      }
    } catch (error) {
      addLog(`❌ Error evaluating session: ${error.message}`, 'error');
    }
    return null;
  };

  const toggleMicrophone = () => {
    // Don't allow toggling mic while AI is speaking
    if (isSpeaking) {
      addLog('Please wait for AI to finish speaking', 'warning');
      return;
    }
    
    // For RTVI client
    if (rtviClientRef.current) {
      const tracks = rtviClientRef.current.tracks();
      if (tracks.local?.audio) {
        const newState = !isMicEnabled;
        tracks.local.audio.enabled = newState;
        setIsMicEnabled(newState);
        setIsListening(newState);
        addLog(newState ? 'Microphone enabled' : 'Microphone disabled');
      }
    }
  };

  const toggleSpeaker = () => {
    if (audioElementRef.current) {
      const newState = !isSpeakerEnabled;
      audioElementRef.current.muted = !newState;
      setIsSpeakerEnabled(newState);
      addLog(newState ? 'Speaker enabled' : 'Speaker disabled');
    }
  };

  const toggleConnection = () => {
    if (isConnected) {
      disconnect();
    } else {
      connect();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (userSpeakingTimeoutRef.current) {
        clearTimeout(userSpeakingTimeoutRef.current);
      }
      if (rtviClientRef.current) {
        if (rtviClientRef.current.ws) {
          rtviClientRef.current.ws.close();
        } else if (rtviClientRef.current.disconnect) {
          rtviClientRef.current.disconnect();
        }
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Format time remaining as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'Connected': return 'text-green-500';
      case 'Connecting': return 'text-yellow-500';
      case 'Error': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-8 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl shadow-2xl">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {interviewType === 'interviewer' ? '🎤 Live Interview' : '🎯 AI Training Session'}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-lg">
            {candidateName} • {jobTitle}
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            {companyName}
          </p>
        </div>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="p-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 bg-white dark:bg-gray-700 rounded-xl shadow-md hover:shadow-lg transition-all"
          title="Settings"
        >
          <Settings size={24} />
        </button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="mb-8 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold mb-6 text-gray-900 dark:text-white">Session Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Language
              </label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={isConnected}
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="en-US">🇺🇸 English (US)</option>
                <option value="en-GB">🇬🇧 English (UK)</option>
                <option value="en-IN">🇮🇳 English (India)</option>
                <option value="hi-IN">🇮🇳 Hindi</option>
                <option value="es-ES">🇪🇸 Spanish</option>
                <option value="fr-FR">🇫🇷 French</option>
                <option value="de-DE">🇩🇪 German</option>
                <option value="ta-IN">IN Tamil</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Voice
              </label>
              <select
                value={voiceGender}
                onChange={(e) => setVoiceGender(e.target.value)}
                disabled={isConnected}
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="female">👩 Female Voice</option>
                <option value="male">👨 Male Voice</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Speech Speed
              </label>
              <select
                value={speechSpeed}
                onChange={(e) => setSpeechSpeed(parseFloat(e.target.value))}
                disabled={isConnected}
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="0.75">🐢 Slow (0.75x)</option>
                <option value="1.0">⚡ Normal (1.0x)</option>
                <option value="1.25">🚀 Fast (1.25x)</option>
              </select>
            </div>
          </div>
          {isConnected && (
            <p className="mt-4 text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2">
              <span>⚠️</span>
              Settings cannot be changed during an active session
            </p>
          )}
        </div>
      )}

      {/* Timer Display */}
      {isConnected && (
        <div className="mb-8 text-center">
          <div className={`inline-flex items-center gap-3 px-8 py-4 rounded-2xl shadow-lg ${
            timeRemaining <= 10 
              ? 'bg-gradient-to-r from-red-500 to-orange-500 animate-pulse' 
              : timeRemaining <= 60
              ? 'bg-gradient-to-r from-amber-500 to-yellow-500'
              : 'bg-gradient-to-r from-blue-500 to-purple-500'
          }`}>
            <span className="text-white text-5xl font-bold font-mono">
              {formatTime(timeRemaining)}
            </span>
            {timeRemaining <= 10 && (
              <span className="text-white text-sm font-semibold">
                ⏰ Wrapping up...
              </span>
            )}
          </div>
        </div>
      )}

      {/* Status Display */}
      <div className="mb-8 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-3">
              <span className={`text-sm font-semibold ${getStatusColor()}`}>
                {connectionStatus}
              </span>
            </div>
            {isSpeaking && (
              <div className="flex items-center space-x-2 text-purple-600">
                <span className="animate-pulse">🎙️</span>
                <span className="text-sm font-medium">AI Speaking...</span>
              </div>
            )}
            {isMicEnabled && !isSpeaking && (
              <div className="flex items-center space-x-2 text-green-600">
                <span className="animate-pulse">🎤</span>
                <span className="text-sm font-medium">Listening...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Control Buttons */}
      <div className="flex justify-center space-x-4 mb-6">
        <button
          onClick={toggleConnection}
          disabled={isConnecting}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium ${
            isConnected 
              ? 'bg-red-500 hover:bg-red-600 text-white' 
              : 'bg-green-500 hover:bg-green-600 text-white'
          } ${isConnecting ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isConnected ? <PhoneOff size={20} /> : <Phone size={20} />}
          <span>
            {isConnecting ? 'Connecting...' : isConnected ? 'End Session' : 'Start Session'}
          </span>
        </button>

        <button
          onClick={toggleMicrophone}
          disabled={!isConnected}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium ${
            isMicEnabled 
              ? 'bg-blue-500 hover:bg-blue-600 text-white' 
              : 'bg-gray-500 hover:bg-gray-600 text-white'
          } ${!isConnected ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isMicEnabled ? <Mic size={20} /> : <MicOff size={20} />}
          <span>{isMicEnabled ? 'Mute' : 'Unmute'}</span>
        </button>

        <button
          onClick={toggleSpeaker}
          disabled={!isConnected}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium ${
            isSpeakerEnabled 
              ? 'bg-purple-500 hover:bg-purple-600 text-white' 
              : 'bg-gray-500 hover:bg-gray-600 text-white'
          } ${!isConnected ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isSpeakerEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
          <span>{isSpeakerEnabled ? 'Speaker On' : 'Speaker Off'}</span>
        </button>
      </div>

      {/* Audio Element */}
      <audio
        ref={audioElementRef}
        autoPlay
        playsInline
        className="hidden"
      />

      {/* Instructions */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Instructions</h3>
        <ul className="text-blue-800 space-y-1">
          <li>1. Click "Start Session" to begin the {interviewType === 'interviewer' ? 'interview' : 'training session'}</li>
          <li>2. Click "Unmute" to enable your microphone when ready to speak</li>
          <li>3. The AI will speak automatically - ensure your speakers are on</li>
          <li>4. Click "End Session" when finished</li>
        </ul>
      </div>
    </div>
  );
};

export default LiveInterview;
